import { useLocation, Link } from "wouter";
import { AppShell, Divider, NavLink, ScrollArea, Stack } from "@mantine/core";
import {
  IconDashboard,
  IconChartHistogram,
  IconRadar,
  IconWorldWww,
  IconFlame,
  IconSearch,
  IconReport,
  IconFileText,
  IconCalendarEvent,
  IconPhoto,
  IconSettings,
  IconFlag,
  IconLogout,
  IconUserStar,
  IconBuildingFactory,
  IconActivity,
  IconServerCog,
  IconBuildingSkyscraper,
  IconLayoutGrid,
  IconUsers,
  IconCategory,
  IconKey,
} from "@tabler/icons-react";
import { useStore } from "./store";
import { userLogout } from "./api";
import { useIsGlobalAdmin } from "./_Shared/useIsGlobalAdmin";
import { useCanUseContent } from "./_Shared/useCanUseContent";

export default function Navbar({ toggle }) {
  const [location] = useLocation();
  const authToken = useStore((state) => state.authToken);
  const profile = useStore((state) => state.profile);
  const isGlobalAdmin = useIsGlobalAdmin();
  const canUseContent = useCanUseContent();

  if (!authToken) {
    return (
      <AppShell.Section component={ScrollArea}>
        <NavLink
          component={Link}
          to="/auth/login"
          label="Login"
          leftSection={<IconKey size={18} />}
          onClick={toggle}
        />
      </AppShell.Section>
    );
  }

  const navItems = [
    { label: "Overview", icon: IconLayoutGrid, path: "/overview" },
    { label: "Dashboard", icon: IconDashboard, path: "/dashboard" },
    { label: "Objective", icon: IconFlag, path: "/objectives" },
    { divider: "AI Visibility" },
    { label: "Timeline", icon: IconChartHistogram, path: "/ai-visibility" },
    { label: "Detection", icon: IconRadar, path: "/ai-detection" },
    { label: "Reports", icon: IconReport, path: "/reports" },
    { label: "LLM Config", icon: IconSettings, path: "/llm-config" },
    { divider: "Analytics" },
    { label: "Web Traffic", icon: IconWorldWww, path: "/web-traffic" },
    { label: "Web Mentions", icon: IconFlame, path: "/web-mentions" },
    { label: "Search Console", icon: IconSearch, path: "/search-console" },
    ...(canUseContent
      ? [
          { divider: "Content" },
          { label: "Content", icon: IconFileText, path: "/content" },
          { label: "Categories", icon: IconCategory, path: "/content/categories" },
          { label: "Clusters", icon: IconCalendarEvent, path: "/plans" },
          { label: "Media", icon: IconPhoto, path: "/media" },
        ]
      : []),
    { divider: "Config" },
    { label: "Brand Config", icon: IconSettings, path: "/settings" },
    ...((profile?.role === "TenantOwner" ||
      profile?.role === "TenantAdmin" ||
      profile?.role === "GlobalAdmin")
      ? [
          { label: "Team", icon: IconUsers, path: "/team" },
          { label: "Tenant Settings", icon: IconBuildingSkyscraper, path: "/settings/tenant" },
        ]
      : []),
  ];

  const adminItems = [
    { label: "Users", icon: IconUserStar, path: "/admin/users" },
    { label: "Tenants", icon: IconBuildingFactory, path: "/tenants" },
    { label: "Monitoring", icon: IconActivity, path: "/admin/monitoring" },
    { label: "Jobs", icon: IconServerCog, path: "/admin/jobs" },
  ];

  return (
    <>
      <AppShell.Section grow component={ScrollArea}>
        <Stack gap={0}>
          {navItems.map((item, i) =>
            item.divider ? (
              <Divider key={i} label={item.divider} mt="sm" mb={4} />
            ) : (
              <NavLink
                key={item.path}
                component={Link}
                to={item.path}
                label={item.label}
                leftSection={<item.icon size={18} />}
                active={
                  item.path === "/settings"
                    ? location === "/settings" || location.startsWith("/settings?")
                    : location === item.path || (item.path !== "/dashboard" && location.startsWith(item.path))
                }
                onClick={toggle}
              />
            )
          )}

          {profile?.role === "GlobalAdmin" && (
            <>
              <Divider label="Admin" mt="sm" mb={4} />
              {adminItems.map((item) => (
                <NavLink
                  key={item.path}
                  component={Link}
                  to={item.path}
                  label={item.label}
                  leftSection={<item.icon size={18} />}
                  active={location.startsWith(item.path)}
                  onClick={toggle}
                />
              ))}
            </>
          )}
        </Stack>
      </AppShell.Section>
      <AppShell.Section>
        <NavLink
          label="Logout"
          leftSection={<IconLogout size={18} />}
          onClick={() => { userLogout(); toggle(); }}
        />
      </AppShell.Section>
    </>
  );
}
